var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import moment from "moment";
import { useEffect, useState } from "react";
import { matchPath, useLoaderData, useLocation, useSearchParams, useParams } from "react-router-dom";
import { Routes } from "../../Shared/components/Navigation/navigationUtils";
import { LogLevel } from "../../Shared/hooks/useLogging/Logging-types";
import { useLogging } from "../../Shared/hooks/useLogging/useLogging";
import { usePriceFormat } from "../../Shared/hooks/usePriceFormat/usePriceFormat";
import { axiosPrivate } from "../../api/axios";
import useToast from "../../hooks/useToast";
import { getDefaultMealSize } from "../components/modules/MealSize/mealSizeUtils";
import { OrderFlowErrorMessages, useSession } from "../hooks/Session/useSession";
import { ActionTypes } from "./OrderFlowActionTypes";
import { useOrderFlow } from "./OrderFlowContext";
import { DEFAULT_DAY_COUNT, ModuleNames, ModuleTypes, } from "./OrderFlowTypes";
import axios from "axios";
import { useCompanyConfigQuery } from "../../SWR/Company/useCompanyConfigQuery";
import { synchronizeActiveCartProducts, synchronizeCartQuestionAnswers, getDefaultCart, getDefaultModel, getDayFilterDays, } from "../hooks/useHelpers";
import { useNavigatePreserveSearch } from "../../Shared/components/Navigation/hooks/useNavigatePreserveSearch";
const defaultLoadingText = "Loading...";
/**
 * Gets the Order Flow Model
 * @returns
 */
export const useGetModel = (editSku, editOrderId, dateChangesTakeEffect, startDate, subscriptionPeriod) => {
    var _a, _b, _c, _d, _e;
    const { sku: skuParam } = useParams();
    const [searchParams] = useSearchParams();
    const { datadogLog } = useLogging();
    const { applyRecipeTypeUpcharge } = useRecipes();
    const { dispatch } = useOrderFlow();
    const [loading, setLoading] = useState(defaultLoadingText);
    const { ContinueToCheckout, restartOrderFlow } = useSession();
    const { startSunday } = useCompanyConfigQuery();
    const toast = useToast();
    const navigate = useNavigatePreserveSearch();
    const location = useLocation();
    const cartGuidParam = searchParams.get("cart");
    const postalCodeSearch = searchParams.get("postalcode");
    const locationSearch = searchParams.get("location");
    const currentSku = editSku ? editSku : skuParam;
    const currentPostalCode = (_b = (_a = location === null || location === void 0 ? void 0 : location.state) === null || _a === void 0 ? void 0 : _a.postalCode) !== null && _b !== void 0 ? _b : postalCodeSearch;
    const currentLocation = (_d = (_c = location === null || location === void 0 ? void 0 : location.state) === null || _c === void 0 ? void 0 : _c.location) !== null && _d !== void 0 ? _d : undefined;
    const currentLocationSku = (_e = currentLocation === null || currentLocation === void 0 ? void 0 : currentLocation.locationSku) !== null && _e !== void 0 ? _e : locationSearch;
    const isCheckout = matchPath("/:sku/Checkout", location.pathname);
    const loadingText = useLoaderData();
    // ========== UPDATE LOADING TEXT IF CONFIGURED ==========
    useEffect(() => {
        let mounted = true;
        if (mounted && loadingText) {
            setLoading(loadingText);
        }
        return () => {
            mounted = false;
        };
    }, [loadingText]);
    // ========== FETCH MODEL ==========
    useEffect(() => {
        if (isCheckout === null) {
            getModel();
        }
    }, []);
    const getModel = () => __awaiter(void 0, void 0, void 0, function* () {
        var _f, _g, _h, _j;
        try {
            const { data: rawData } = yield axiosPrivate.get(`/OrderFlow/GetModel`, {
                params: {
                    timestamp: new Date().getTime(),
                    zip: currentPostalCode,
                    locationSku: currentLocationSku,
                    sku: currentSku,
                    editOrderId: editOrderId,
                    dateChangesTakeEffect: dateChangesTakeEffect,
                    startDate: startDate,
                    requestedCartGuid: cartGuidParam,
                },
                responseType: "json",
                headers: { Accept: "application/json" },
            });
            // Ensure data is parsed correctly
            const data = JSON.parse(JSON.stringify(rawData));
            if (!data.success) {
                if (data.invalidSku) {
                    datadogLog(`Got to Order Flow with an invalid flow Sku: ${currentSku} `, LogLevel.WARN);
                    navigate(Routes.NOT_FOUND, { replace: true, state: Object.assign(Object.assign({}, location.state), { prevRoute: location.pathname }) });
                    return;
                }
                if (data.expiredCart) {
                    datadogLog(`Tried to load an expired cart via URL. CartGuid: ${cartGuidParam} `, LogLevel.WARN);
                    toast.push(OrderFlowErrorMessages.EXPIRED_CART, { variant: "info" });
                    navigate(`/${currentSku}/Location`, { replace: true });
                    return;
                }
                restartOrderFlow(currentSku, undefined, false);
                return;
            }
            // Check if any module is visible
            let isAnyModuleVisible = false;
            data.model.Flow.FlowModules.forEach((flowModule) => {
                try {
                    flowModule.ModuleConfig = JSON.parse(flowModule.ModuleConfig);
                    flowModule.Module.AvailableConfig = JSON.parse(flowModule.Module.AvailableConfig);
                    if (flowModule.ModuleConfig["Visible"] === undefined ||
                        flowModule.ModuleConfig["Visible"] === null ||
                        flowModule.ModuleConfig["Visible"] === true) {
                        isAnyModuleVisible = true;
                    }
                }
                catch (e) {
                    datadogLog(`Caught exception while validating Flow Modules. Message: ${e.message}`, LogLevel.ERROR, undefined, e);
                    restartOrderFlow(currentSku);
                }
            });
            // Determine starting page
            const pageToStart = ((_f = location.state) === null || _f === void 0 ? void 0 : _f.page) > 0 ? (_g = location.state) === null || _g === void 0 ? void 0 : _g.page : 1;
            // Prepare cart
            let defaultCart;
            if (!!isCheckout && !data.model.SavedCart) {
                datadogLog("Got to Checkout with an expired cart", LogLevel.INFO);
                toast.push(OrderFlowErrorMessages.EXPIRED_CART, { variant: "info" });
                navigate(`/${currentSku}/Location`, { replace: true });
                return;
            }
            if (data.model.SavedCart && !data.model.IsEditFlow) {
                defaultCart = JSON.parse(data.model.SavedCart.CartJson);
                // When we save the cart and call JSON.stringify all moment objects are turned into strings.
                // If we have these values in the saved cart they must be converted to moment objects
                defaultCart.selectedDay = defaultCart.selectedDay ? moment(defaultCart.selectedDay) : undefined;
                defaultCart.startDate = defaultCart.startDate ? moment(defaultCart.startDate) : undefined;
                defaultCart.cutoffDay = defaultCart.cutoffDay ? moment(defaultCart.cutoffDay) : undefined;
                defaultCart.currentPage = pageToStart;
                synchronizeActiveCartProducts(defaultCart.products, data.model.Products);
                synchronizeCartQuestionAnswers(defaultCart.questionAnswers, data.model.Flow.FlowModules.filter((module) => module.ModuleName === ModuleNames.QUESTIONS));
            }
            else {
                if (!(currentPostalCode || data.model.PostalCode) && !currentLocation && !data.model.IsEditFlow) {
                    datadogLog("Got to Order Flow without a location or zip", LogLevel.WARN);
                    navigate(`/${currentSku}/Location`, { replace: true });
                    return;
                }
                if (currentPostalCode)
                    data.model.PostalCode = currentPostalCode;
                if (currentLocation)
                    data.model.Location = currentLocation;
                defaultCart = getDefaultCart(data.model, startSunday, pageToStart, subscriptionPeriod);
            }
            const defaultModel = getDefaultModel(Object.assign(Object.assign({}, data.model), { Recipes: [], RecipeFetchStatus: { error: false, completed: false } }));
            if (editOrderId)
                defaultCart.editOrderId = editOrderId;
            // Redirect to checkout if no modules are visible
            if (!isAnyModuleVisible) {
                setLoading("Redirecting...");
                try {
                    const res = yield ContinueToCheckout(defaultCart, defaultModel);
                    if (res && res.success && res.continueToCheckout) {
                        const orderFlowState = {
                            cart: defaultCart,
                            model: defaultModel,
                            pricingBreakdown: undefined,
                            sku: defaultModel.Sku,
                        };
                        navigate({ pathname: `/${defaultModel.Sku}/Checkout` }, { state: Object.assign(Object.assign({}, location.state), { orderFlowState }) });
                    }
                    else {
                        datadogLog("Failed to navigate to checkout in getModel", LogLevel.ERROR);
                        restartOrderFlow(currentSku);
                    }
                }
                catch (err) {
                    datadogLog(`Caught exception trying ContinueToCheckout. Message: ${err.message}`, LogLevel.ERROR, undefined, err);
                    restartOrderFlow(currentSku);
                }
            }
            dispatch({ type: ActionTypes.SET_MODEL, payload: defaultModel });
            // Handle meal size module for edit flow
            const mealSizeModule = data.model.Flow.FlowModules.find((fm) => fm.Module.ModuleType === ModuleTypes.MEAL_SIZE);
            if (mealSizeModule && defaultModel.IsEditFlow) {
                const mealSizeModuleConfig = mealSizeModule.ModuleConfig;
                defaultCart.mealSize = getDefaultMealSize(mealSizeModuleConfig === null || mealSizeModuleConfig === void 0 ? void 0 : mealSizeModuleConfig.DefaultSelectionMode, defaultModel.SubscriptionMeals, defaultModel.Program.ProgramMeals, defaultModel.MealSizes);
            }
            // Apply recipe type upcharges
            const { updatedProgramMeals } = applyRecipeTypeUpcharge(defaultModel, undefined, defaultCart.recipeType, [], []);
            dispatch({ type: ActionTypes.SET_PROGRAM_MEALS, payload: updatedProgramMeals });
            // Day filter logic
            const dayFilterModule = defaultModel.Flow.FlowModules.find((fm) => fm.Module.ModuleType === ModuleTypes.DAY_FILTER);
            const dayFilterConfig = dayFilterModule === null || dayFilterModule === void 0 ? void 0 : dayFilterModule.ModuleConfig;
            if (dayFilterConfig) {
                const dayFilterDays = getDayFilterDays(defaultCart.daysOfWeek, undefined, defaultModel.DeliveryDays, defaultCart.startDate, defaultCart.isSubscription, defaultCart.subscriptionPeriod, defaultCart.oneTimeOrderPeriod, dayFilterConfig.OnlySelectedDaysOfWeek, dayFilterConfig.OnlyDaysWithDefaultMeals, defaultModel.GeneralSettings.OnlyShowDeliveryDays, (_h = dayFilterConfig.DayCount) !== null && _h !== void 0 ? _h : DEFAULT_DAY_COUNT);
                let selectedDay = defaultCart.selectedDay;
                if (!selectedDay || !dayFilterDays.find((d) => moment(d).isSame(selectedDay, "date"))) {
                    selectedDay = dayFilterDays.length > 0 ? dayFilterDays[0] : undefined;
                }
                dispatch({ type: ActionTypes.SET_SELECTED_DAY, payload: moment(selectedDay) });
                dispatch({ type: ActionTypes.SET_DAY_FILTER_DAYS, payload: dayFilterDays });
            }
            // Preserve original selections in cart
            const originalSelections = {
                daysOfWeek: defaultCart.daysOfWeek,
                mealSize: defaultCart.mealSize,
                mealQuantity: defaultCart.meals.map((meal) => ({
                    programMealId: meal.ProgramMealId,
                    mealDate: meal.MealDate,
                    quantity: meal.Quantity,
                })),
                mealBundles: defaultCart.bundles.flatMap((bundle) => ({
                    name: bundle.BundleName,
                    quantity: bundle.Quantity,
                    meals: bundle.BundleMeals.map((bundleMeal) => ({
                        programMealId: bundleMeal.ProgramMealId,
                        quantity: bundleMeal.Quantity,
                        dayOfWeek: bundleMeal.DayOfWeek,
                    })),
                })),
                products: defaultCart.products
                    .filter((p) => p.quantity > 0)
                    .map((product) => ({
                    id: product.productId,
                    quantity: product.quantity,
                    dayOfWeek: product.DayOfWeek,
                })),
            };
            defaultCart.originalSelections = originalSelections;
            dispatch({ type: ActionTypes.SET_CART, payload: defaultCart });
        }
        catch (err) {
            if (axios.isAxiosError(err)) {
                datadogLog(err.message, LogLevel.ERROR, (_j = err.response) === null || _j === void 0 ? void 0 : _j.data, err);
            }
            else if (err instanceof Error) {
                datadogLog(err.message, LogLevel.ERROR, undefined, err);
            }
            restartOrderFlow(currentSku);
        }
        finally {
            setLoading(undefined);
        }
    });
    return { loading, setLoading, getModel };
};
export const useRecipes = () => {
    const [loading, setLoading] = useState(false);
    const { store } = useOrderFlow();
    const { formatPrice } = usePriceFormat();
    const getSelectedRecipes = (payload) => __awaiter(void 0, void 0, void 0, function* () {
        setLoading(true);
        const response = yield axiosPrivate.post(`/OrderFlow/GetSelectedRecipes`, payload);
        const data = response.data;
        setLoading(false);
        return data;
    });
    const applyRecipeTypeUpcharge = (model, oldRecipeType, newRecipeType, cartMeals, template) => {
        var _a, _b;
        const oldRecipeTypeUpcharge = oldRecipeType && oldRecipeType.recipeTypePrice >= 0 ? oldRecipeType.recipeTypePrice : 0;
        const newRecipeTypeUpcharge = newRecipeType && newRecipeType.recipeTypePrice >= 0 ? newRecipeType.recipeTypePrice : 0;
        const newUpcharge = newRecipeTypeUpcharge - oldRecipeTypeUpcharge;
        const updatedProgramMeals = (_b = (_a = model === null || model === void 0 ? void 0 : model.Program) === null || _a === void 0 ? void 0 : _a.ProgramMeals) === null || _b === void 0 ? void 0 : _b.map((programMeal) => {
            var _a;
            const newPrice = programMeal.Price + newUpcharge;
            // PriceDisplay must stay a formatted string (it mirrors the backend's ProgramMeal.PriceDisplay)
            // — formatPrice() returns an ItemPriceObject, so pull just the text out of it.
            // Falls back to the previous string if formatPrice can't produce one.
            const formattedPrice = model
                ? (_a = formatPrice({ displayPriceOverride: newPrice }).itemPrice) !== null && _a !== void 0 ? _a : programMeal.PriceDisplay
                : programMeal.PriceDisplay;
            return Object.assign(Object.assign({}, programMeal), { Price: newPrice, PriceDisplay: formattedPrice });
        });
        const updatedCartMeals = cartMeals === null || cartMeals === void 0 ? void 0 : cartMeals.map((meal) => {
            var _a;
            const newAssignments = (_a = meal.RecipeAssignments) === null || _a === void 0 ? void 0 : _a.map((recipe) => {
                if (!(recipe === null || recipe === void 0 ? void 0 : recipe.RecipeAssignmentPrice))
                    return recipe;
                const newRecipePrice = recipe.RecipeAssignmentPrice + newUpcharge;
                return Object.assign(Object.assign({}, recipe), { RecipePrice: newRecipePrice, RecipeAssignmentPrice: newRecipePrice });
            });
            return Object.assign(Object.assign({}, meal), { RecipeAssignments: newAssignments, Price: meal.Price + newUpcharge });
        });
        const updatedTemplate = template === null || template === void 0 ? void 0 : template.map((templateMeal) => {
            const newPrice = templateMeal.Price + newUpcharge;
            return Object.assign(Object.assign({}, templateMeal), { Price: newPrice });
        });
        return { updatedProgramMeals, updatedTemplate, updatedCartMeals };
    };
    const recipeModuleDisplayRequirementCheck = (requirement) => {
        let requirementResponse = undefined;
        const requirements = store.model.requirements;
        const req = requirements.find((r) => r.page == store.cart.currentPage);
        const requirementsModuleConfig = req ? req.moduleConfig : undefined;
        const EnforceRecipeTypeSelection = requirementsModuleConfig
            ? requirementsModuleConfig.EnforceRecipeTypeSelection
            : undefined;
        if (requirement && EnforceRecipeTypeSelection && !store.cart.recipeType) {
            requirementResponse = generateResponse(false, requirement);
        }
        return requirementResponse;
    };
    const generateResponse = (meetsRequirement, requirement) => {
        let errorMsg = "N/A";
        if (requirement) {
            if (requirement.ErrorMsg) {
                errorMsg = requirement.ErrorMsg;
            }
            else if (requirement.TextValue) {
                errorMsg = requirement.TextValue;
            }
        }
        return {
            meetsRequirements: meetsRequirement,
            errorMessage: errorMsg,
        };
    };
    return { getSelectedRecipes, applyRecipeTypeUpcharge, recipeModuleDisplayRequirementCheck, loading };
};
/**
 * Loader function to fetch the `LoadingText` property from the Order Flow configurable text settings.
 * This text is used to display a loading message while the order model is being retrieved.
 *
 * If the request is successful, the function returns the `LoadingText` property from the server response.
 * If an error occurs or the `LoadingText` is missing, a default loading message is returned instead.
 *
 * @param {Params<string>} params - The parameters passed to the loader, typically containing the SKU.
 * @returns {Promise<string>} - A promise that resolves to the `LoadingText` string or a default value if an error occurs.
 */
export const OrderFlowLoader = (params) => {
    return axiosPrivate
        .get(`/Account/OrderFlowLevelConfigurableTextSettings?sku=${params.sku}`)
        .then((res) => res.data.ConfigurableTextSettings.LoadingText || defaultLoadingText)
        .catch((e) => {
        console.error(e);
        return defaultLoadingText;
    });
};
